<template>
  <RightSheet style="width: 80vw;" v-model="dialogVisible" max-width="1920">
    <div class="dialog-wrapper">
      <!-- 标题 -->
      <div class="dialog-title">
        <v-btn icon variant="text" class="back-btn" @click="closeDialog">
          <v-icon>mdi-reply-outline</v-icon>
          返回
        </v-btn>
        PACK消防信息 <span v-if="block" class="ml-2 font-weight-bold">{{ block.eblock_name }}</span>
      </div>

      <!-- 主内容区域 -->
      <div class="dialog-main">
        <v-card class="cluster-detail-table rounded-xl card-bg card-bd d-flex flex-column overflow-hidden"
          variant="elevated" elevation="0">
          <template #title>
            <div class="d-flex justify-space-between align-center">
              <div class="text-subtitle-1 font-weight-bold">
                <!-- 预留标题位置或空白 -->
              </div>
              <!-- 可以放筛选条件等 -->
            </div>
          </template>

          <div class="d-flex flex-column flex-grow-1 overflow-hidden">
            <v-table density="default" class="bg-transparent custom-table flex-grow-1 overflow-auto" height="100%">
              <thead>
                <tr>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">Pack</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d; min-width: 140px;">Pack消防预警级别</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">Pack温度(℃)</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">Pack湿度(%)</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">CO浓度(ppm)</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d; min-width: 180px;">voc(可燃气体浓度)(ppm)
                  </th>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">烟雾浓度(ppm)</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">CO状态</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">VOC状态</th>
                  <th class="text-left font-weight-regular" style="color: #444a6d;">Pack状态</th>
                  <th class="text-left font-weight-regular fixed-col-right" style="color: #444a6d; min-width: 160px;">
                    消防探测器在线状态
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(pack, index) in paginatedPackList" :key="index">
                  <td style="color: #444a6d;">{{ pack.pack_index || pack.index || '--' }}</td>
                  <td>
                    <span :class="['status-badge', getAlarmLevelDesc(pack.alarm_level).class]">
                      {{ getAlarmLevelDesc(pack.alarm_level).text }}
                    </span>
                  </td>
                  <td style="color: #444a6d;">{{ pack.temp ?? '--' }}</td>
                  <td style="color: #444a6d;">{{ pack.hum ?? '--' }}</td>
                  <td style="color: #444a6d;">{{ pack.co_conc ?? '--' }}</td>
                  <td style="color: #444a6d;">{{ pack.voc_conc ?? '--' }}</td>
                  <td style="color: #444a6d;">{{ pack.smoke_conc ?? '--' }}</td>
                  <td>
                    <span :class="['status-badge', getNormalAlarmDesc(pack.co_alarm).class]">
                      {{ getNormalAlarmDesc(pack.co_alarm).text }}
                    </span>
                  </td>
                  <td>
                    <span :class="['status-badge', getNormalAlarmDesc(pack.voc_alarm).class]">
                      {{ getNormalAlarmDesc(pack.voc_alarm).text }}
                    </span>
                  </td>
                  <td>
                    <span :class="['status-badge', getThermalRunawayDesc(pack.pack_st).class]">
                      {{ getThermalRunawayDesc(pack.pack_st).text }}
                    </span>
                  </td>
                  <td class="fixed-col-right">
                    <span :class="['status-badge', getOfflineDesc(pack.offline).class]">
                      {{ getOfflineDesc(pack.offline).text }}
                    </span>
                  </td>
                </tr>
                <tr v-if="paginatedPackList.length === 0">
                  <td colspan="11" class="text-center text-grey py-4">暂无数据</td>
                </tr>
              </tbody>
            </v-table>
            <TablePagination v-model="packPageNum" :total="packList.length" :page-size="packPageSize"
              @change="handlePackPageChange" />
          </div>
        </v-card>
      </div>

      <!-- 关闭按钮 -->
      <v-btn icon size="small" variant="text" class="close-btn" @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <div v-if="false">{{ Object.keys(block) }}</div>
  </RightSheet>
</template>

<script setup>
import { ref, computed, watch, toRaw, nextTick } from 'vue'
import TablePagination from '@/components/TablePagination/index.vue'
import RightSheet from '@/components/RightSheet/index.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  block: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

// 分页相关变量
const packList = ref([])
const packPageNum = ref(1)
const packPageSize = ref(10)
const packJumpPage = ref(1)
const pageSizeItems = [10, 20, 50, 100]

// 计算分页数据
const packTotalPages = computed(() => {
  return Math.ceil((packList.value.length || 0) / packPageSize.value) || 1
})

const paginatedPackList = computed(() => {
  const list = packList.value || []
  const start = (packPageNum.value - 1) * packPageSize.value
  const end = start + packPageSize.value
  return list.slice(start, end)
})

const closeDialog = () => {
  dialogVisible.value = false
  emit('close')
}

// 报警等级描述
const getAlarmLevelDesc = (level) => {
  switch (level) {
    case 0: return { text: '正常', class: 'online' }
    case 1: return { text: '一级预警', class: 'warning' }
    case 2: return { text: '二级预警', class: 'warning' }
    case 3: return { text: '三级预警', class: 'error' }
    default: return { text: '未知', class: 'offline' }
  }
}

// 通用告警描述 (CO, VOC)
const getNormalAlarmDesc = (status) => {
  switch (status) {
    case 0: return { text: '正常', class: 'online' }
    case 1: return { text: '告警', class: 'error' }
    default: return { text: '--', class: '' }
  }
}

// 热失控描述 (Pack状态)
const getThermalRunawayDesc = (status) => {
  switch (status) {
    case 0: return { text: '正常', class: 'online' }
    case 1: return { text: '热失控', class: 'error' }
    default: return { text: '--', class: '' }
  }
}

// 在线状态描述
const getOfflineDesc = (status) => {
  switch (status) {
    case 0: return { text: '离线', class: 'offline' }
    case 1: return { text: '在线', class: 'online' }
    default: return { text: '离线', class: 'offline' }
  }
}

// 分页处理
const handlePackPageChange = (val) => {
  packPageNum.value = val
  packJumpPage.value = val
}

const handlePackSizeChange = (val) => {
  packPageSize.value = val
  packPageNum.value = 1
  packJumpPage.value = 1
}

const handlePackJumpToPage = () => {
  let page = parseInt(packJumpPage.value)
  if (isNaN(page) || page < 1) page = 1
  if (page > packTotalPages.value) page = packTotalPages.value
  packJumpPage.value = page
  packPageNum.value = page
}

const handlePackInputKeyup = (e) => {
  if (e.key === 'Enter') {
    handlePackJumpToPage()
  }
}

// 更新数据逻辑
const updatePackData = () => {
  if (!props.modelValue || !props.block) {
    return
  }

  // 使用 toRaw 获取原始对象，避免 Proxy 干扰
  const rawBlock = toRaw(props.block)
  // 尝试获取 pack 或 packs
  let data = rawBlock.pack || rawBlock.packs

  // 如果 data 是对象且非数组（即 Map 形式 {"1": {...}, "2": {...}}），转换为数组
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    data = Object.values(data)
  }

  if (Array.isArray(data)) {
    // 确保按 pack_index 排序
    packList.value = data.sort((a, b) => {
      return (Number(a.pack_index) || 0) - (Number(b.pack_index) || 0)
    })
  } else {
    packList.value = []
  }

  // 重置分页
  packPageNum.value = 1
  packJumpPage.value = 1
}

// 监听弹框显示状态
watch(() => props.modelValue, (val) => {
  if (val) {
    nextTick(() => {
      updatePackData()
    })
  }
}, { immediate: true })

// 监听 block 数据变化
watch(() => props.block, (newBlock) => {
  if (props.modelValue && newBlock) {
    updatePackData()
  }
}, { deep: true })
</script>

<style scoped lang="scss">
.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 100vh;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
  display: flex;
  flex-direction: column;
}

.dialog-title {
  display: flex;
  align-items: center;
  padding-left: 24px;
  margin-bottom: 20px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  position: relative;
  flex-shrink: 0;
}

.back-btn {
  width: 98px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #C0CFDA;
  margin-right: 20px;
  font-size: 14px;
  color: #2E2E2E;
  justify-content: center;
  padding: 0;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
  width: 48px;
  height: 48px;
  color: #2E2E2E;
}

.dialog-main {
  height: calc(100% - 83px);
  /* background: rgba(246, 251, 255, 1); matched EnergyStorage */
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Reuse table styles */
.card-bg {
  background: transparent !important;
  /* Overriding card bg since main has bg */
  border: none !important;
}

.card-bd {
  border: none !important;
}

.cluster-detail-table {
  height: 100%;
  box-shadow: none !important;
  background: #fff !important;

}


/* Table custom styles */
.custom-table {
  :deep(th) {
    font-size: 14px !important;
    height: 48px !important;
    border-bottom: 1px solid #E0E4E7 !important;
    // background-color: rgba(231, 231, 231, 0.66) !important;
  }

  :deep(td) {
    font-size: 14px !important;
    height: 52px !important;
    border-bottom: 1px solid #E0E4E7 !important;
  }
}

/* Status styles */
.text-error {
  color: #FF4D4F;
  font-weight: bold;
}

.text-success {
  color: #52C41A;
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 14px;
  color: #444A6D;

  &.online {
    background: #DDF3D6;
    color: #36B60C;
    // border: 1px solid #52C41A;
  }

  &.warning {
    background: rgba(250, 173, 20, 0.1);
    color: #FAAD14;
    border: 1px solid #FAAD14;
  }

  &.error {
    background: rgba(255, 77, 79, 0.1);
    color: #FF4D4F;
    border: 1px solid #FF4D4F;
  }

  &.offline {
    background: #F4F7FC;
    color: #6B6C6D;
    // opacity: 0.25;
    // border: 1px solid #d9d9d9;
  }
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: transparent;
  flex-shrink: 0;
  border-top: 1px solid #f0f0f0;

  :deep(.v-btn--disabled) {
    color: #fff !important;
    opacity: 1 !important;
  }
}

.pagination-info {
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 22px;
  letter-spacing: 0%;
  color: rgba(0, 0, 0, 0.6);
  vertical-align: middle;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
}

.page-size-select {
  width: 112px;
  flex-shrink: 0;

  :deep(.v-field) {
    background: #fff !important;
    border: 1px solid #dcdcdc !important;
    border-radius: 3px !important;
    min-height: auto !important;
    height: auto !important;
    padding: 5px 8px !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
  }

  :deep(.v-field__input) {
    padding: 0 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: rgba(0, 0, 0, 0.9) !important;
    min-height: auto !important;
    height: 22px !important;
    line-height: 22px !important;
    display: flex !important;
    align-items: center !important;
  }

  :deep(.v-select__selection) {
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: rgba(0, 0, 0, 0.9) !important;
    line-height: 22px !important;
    margin: 0 !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.v-field__outline) {
    display: none !important;
  }

  :deep(.v-field__append-inner .v-icon) {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
    color: #2E2E2E !important;
    opacity: 0.22;
  }
}

.custom-pagination {
  flex-shrink: 0;

  :deep(.v-pagination__list) {
    align-items: center;
    gap: 8px !important;
    margin: 0 !important;
  }

  :deep(.v-pagination__item--is-active) {
    color: #000000E6 !important;
    border: none !important;
    box-shadow: none !important;

    .v-btn {
      background-color: #3A68FF !important;
      color: #fff !important;
      border: none !important;

      &:hover {
        background-color: #3A68FF !important;
        color: #fff !important;
        border: none !important;
      }
    }
  }

  :deep(.v-btn) {
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 3px !important;
    margin: 0 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 22px !important;
    border: 1px solid #DCDCDC !important;
    background: transparent !important;
    color: #000000E6 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    transition: none !important;
    box-shadow: none !important;
    text-align: center !important;

    &:hover {
      background: transparent !important;
      color: #000000E6 !important;
    }

    .v-pagination__item--text {
      line-height: 22px !important;
      font-family: 'PingFang SC', sans-serif !important;
      font-size: 14px !important;
      color: #000000E6 !important;
    }
  }

  :deep(.v-pagination__prev),
  :deep(.v-pagination__next) {
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 3px !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: none !important;
    box-shadow: none !important;

    .v-btn {
      border: none !important;
    }

    &:hover:not(.v-pagination__item--disabled) {
      background: transparent !important;
    }

    &.v-pagination__item--disabled {
      opacity: 0.4 !important;
      cursor: not-allowed !important;
    }
  }

  :deep(.v-pagination__prev .v-icon),
  :deep(.v-pagination__next .v-icon) {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
    color: rgba(0, 0, 0, 1) !important;
  }

  /* 省略号（ellipsis）样式 */
  :deep(.v-pagination__item--ellipsis) {
    border: none !important;
    background: #fff !important;
    color: #000000E6 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
  }

  :deep(.v-btn--variant-text) {
    border: 1px solid #DCDCDC !important;
    background: transparent !important;
    color: #000000E6 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    box-shadow: none !important;
  }
}

.jump-to-page {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  background: #e3e3e3;
  padding: 2px 16px;
  border-radius: 3px;
  line-height: 22px;
  height: 34px;
  width: 160px;
  flex-shrink: 0;

  span {
    white-space: nowrap;
    line-height: 22px;
    font-weight: 400;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
  }

  .jump-input {
    flex: 1 0 0;
    min-width: 0;

    :deep(.v-field) {
      background: #fff !important;
      border: 1px solid #dcdcdc !important;
      border-radius: 3px !important;
      min-height: auto !important;
      height: auto !important;
      margin: 0 !important;
      box-shadow: none !important;
      display: flex !important;
      align-items: center !important;
    }

    :deep(.v-field__input) {
      padding: 0 !important;
      text-align: center !important;
      font-family: 'PingFang SC', sans-serif !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      color: rgba(0, 0, 0, 0.9) !important;
      min-height: auto !important;
      height: 22px !important;
      line-height: 22px !important;
    }

    :deep(.v-field__outline) {
      display: none !important;
    }

    :deep(input) {
      text-align: center !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* 隐藏数字输入框的上下箭头 */
    :deep(input[type="number"]) {

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
        display: none !important;
      }
    }
  }
}
</style>
